import { AlertTypesEnum, IQueryExportCountModel } from '../../../utilities/Constants';
import { IOrganizationRoleModel, IUserModel } from '../../../interfaces/IUser';
import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';

import { IAlertUserUpdateModel } from '../../../interfaces/INote';
import { LocalRoutes } from '../../../utilities/LocalRoutes';
import { SignalRMethods } from '../../../utilities/SignalR';
import { SwitchingZonesModal } from './SwitchingZonesModal';
import { ZoneSelectionModal } from './ZoneSelectionModal';
import { cloneDeep } from 'lodash';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useGetUserUnreadQueryExports } from '../../../shared/react-query-hooks/useGetUserUnreadQueryExports';
import { useSignalRContext } from '../../../contexts/SignalRContext';
import { useZoneContext } from '../../../contexts/useZoneContext';

export function UserUnreadQueryExportStatus(props: {
    user: IUserModel;
    onZoneChange: (org: IOrganizationRoleModel, redirectPath?: string) => void;
}) {
    const { user } = useAuthContext();
    const { alertHub } = useSignalRContext();
    const location = useLocation();
    const zoneContext = useZoneContext();

    const [zonesWithUnreadQueryExports, setZonesWithUnreadQueryExports] = useState<
        IQueryExportCountModel[]
    >([]);
    const [showZoneSelectionModal, setShowZoneSelectionModal] = useState<boolean>(false);
    const [showSwitchingZonesModal, setShowSwitchingZonesModal] = useState<boolean>(false);
    const [hideIcon, setHideIcon] = useState<boolean>(true);

    const unreadQueryExports = useGetUserUnreadQueryExports(props.user);

    const zoneGuidsWithAlerts = zonesWithUnreadQueryExports.map((zone) => zone.orgId);

    const bindSignalrEvents = () => {
        const handleAvailableReports = (payload: IAlertUserUpdateModel) => {
            const { alertUpdates } = payload;

            setZonesWithUnreadQueryExports((prev) => {
                const prevCopy = cloneDeep(prev);

                alertUpdates.forEach((update) => {
                    if (update && update.organization && update.organization.guid) {
                        const orgId = update.organization.guid ?? '';
                        update.changeByType
                            .filter((changeByType) => changeByType.type === AlertTypesEnum.Report)
                            .forEach((changeByType) => {
                                const zoneForThisOrgId = prevCopy.find(
                                    (zone) => zone.orgId === orgId
                                );
                                if (zoneForThisOrgId) {
                                    zoneForThisOrgId.count += changeByType.change;
                                } else {
                                    prevCopy.push({ orgId, count: changeByType.change });
                                }
                            });
                    }
                });

                return [...prevCopy];
            });
        };

        if (alertHub !== undefined) {
            console.log(
                `SignalR - Alert Hub => Listening for ${SignalRMethods.SendQueryExportNotification}`
            );
            alertHub?.on(SignalRMethods.SendQueryExportNotification, handleAvailableReports);
        }
    };

    const unbindSignalrEvents = () => {
        console.log(
            `SignalR - Alert Hub /Stopped listening for ${SignalRMethods.SendQueryExportNotification}`
        );
        alertHub?.off(SignalRMethods.SendQueryExportNotification);
    };

    useEffect(() => {
        unreadQueryExports.refetch();
    }, [location.key]);

    useEffect(() => {
        bindSignalrEvents();
        return unbindSignalrEvents;
    }, [alertHub]);

    const handleIconClick = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        unreadQueryExports.refetch();

        const currentOrganization = zoneContext.zoneGuid;

        const moreThanOneZoneHasUnreadExports =
            (zonesWithUnreadQueryExports.filter((zone) => zone.count > 0)?.length ?? 0) > 1;

        const zoneWithChangesIsNotCurrentZone =
            ((zoneGuidsWithAlerts?.length ?? 0) === 1 &&
                currentOrganization !== zoneGuidsWithAlerts?.[0]) ||
            ((zoneGuidsWithAlerts?.length ?? 0) >= 1 &&
                zonesWithUnreadQueryExports.find(
                    (a) => a.count > 0 && currentOrganization !== a.orgId
                ));

        if (moreThanOneZoneHasUnreadExports) {
            e.preventDefault();
            setShowZoneSelectionModal(true);
        } else if (zoneWithChangesIsNotCurrentZone) {
            e.preventDefault();
            setShowSwitchingZonesModal(true);
        } else {
            zoneContext.zoneNavigate(LocalRoutes.MyDocuments);
        }
    };

    const handleSwitchingZonesSubmit = () => {
        const firstAndOnlyMatchingOrg = zonesWithUnreadQueryExports?.find(
            (a) => a.count >= 1
        )?.orgId;
        const chosenOrg = user.organizationRoles?.find(
            (org) => org.guid === firstAndOnlyMatchingOrg
        );

        if (chosenOrg) {
            setShowSwitchingZonesModal(false);
            props.onZoneChange(chosenOrg, LocalRoutes.MyDocuments);
        }
    };

    const handleZoneSelection = (zoneGuid: string) => {
        const isCurrentZone = zoneGuid === zoneContext.zoneGuid;

        if (isCurrentZone) {
            setShowZoneSelectionModal(false);
            zoneContext.zoneNavigate(LocalRoutes.MyDocuments);
            return;
        }

        const chosenOrg = user.organizationRoles?.find((org) => org.guid === zoneGuid);

        if (chosenOrg) {
            props.onZoneChange(chosenOrg, LocalRoutes.MyDocuments);
            setShowZoneSelectionModal(false);
        }
    };

    useEffect(() => {
        if (!unreadQueryExports.isFetching && unreadQueryExports.status === 'success') {
            const data = unreadQueryExports.data ?? [];
            setZonesWithUnreadQueryExports(data);
            setHideIcon(data.length === 0);
        }
    }, [unreadQueryExports.isFetching, unreadQueryExports.data]);

    useEffect(() => {
        if (!unreadQueryExports.isFetching && unreadQueryExports.status === 'success') {
            setHideIcon(zonesWithUnreadQueryExports.length === 0);
        }
    }, [zonesWithUnreadQueryExports]);

    if (hideIcon && zonesWithUnreadQueryExports.length === 0) {
        return null;
    }

    return (
        <>
            <Link
                to={zoneContext.buildZoneUrl(LocalRoutes.MyDocuments)}
                className="align-self-center"
                style={{ marginLeft: 5 }}
                onClick={handleIconClick}
            >
                <i className="fal fa-file-exclamation color-yellow" />
            </Link>
            {!unreadQueryExports.isLoading && !unreadQueryExports.isFetching && (
                <>
                    <SwitchingZonesModal
                        onCancel={() => setShowSwitchingZonesModal(false)}
                        onClose={() => setShowSwitchingZonesModal(false)}
                        onSubmit={handleSwitchingZonesSubmit}
                        open={showSwitchingZonesModal}
                    />
                    <ZoneSelectionModal
                        onCancel={() => setShowZoneSelectionModal(false)}
                        onClose={() => setShowZoneSelectionModal(false)}
                        onSubmit={handleZoneSelection}
                        open={showZoneSelectionModal}
                        zoneGuids={zonesWithUnreadQueryExports.map((zone) => zone.orgId)}
                        counts={zonesWithUnreadQueryExports}
                    />
                </>
            )}
        </>
    );
}
